import { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiPackage, FiPlus } from "react-icons/fi";
import useMerchantProduct from "../../../Routes/Marketplace/Merchant/lib/fetchMerchantProduct";
import { isLowStock } from "../utils/orderHelpers";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import EmptyState from "../../components/EmptyState";
import Spinner from "../../../Spinner/Spinner";
import ProductPriceDisplay, { ProductDiscountBadge } from "../../../Routes/Marketplace/components/ProductPriceDisplay";
import {
  getProductApprovalBadgeTone,
  getProductApprovalLabel,
  getProductApprovalStatus,
} from "../../utils/productApprovalDisplay";

const PLACEHOLDER = "https://via.placeholder.com/200x200?text=No+Image";

const MerchantProductsPage = () => {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useMerchantProduct();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProducts = data?.pages.flatMap((page) =>
    page.products.filter(
      (p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.category || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const observerRef = useRef();
  const lastRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) fetchNextPage();
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage]
  );

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="Manage your catalog"
        icon={FiPackage}
        actions={
          <Link to="/merchant/products/new" className="dashboard-btn dashboard-btn--primary">
            <FiPlus /> Add product
          </Link>
        }
      />
      <FilterBar searchValue={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder="Search products…" />
      {isLoading && <Spinner />}
      {isError && <p style={{ color: "var(--dash-danger)" }}>Failed to load products.</p>}
      {!isLoading && !isError && filteredProducts?.length === 0 && (
        <EmptyState
          icon={FiPackage}
          title="No products yet"
          message="Add your first product to start selling on the marketplace."
          actionLabel="Add product"
          actionTo="/merchant/products/new"
        />
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
        {filteredProducts?.map((product, index) => {
          const card = (
            <div className="dashboard-card" style={{ margin: 0, padding: 0, overflow: "hidden", position: "relative" }}>
              <img
                src={product.images?.[0] || PLACEHOLDER}
                alt={product.name}
                style={{ width: "100%", height: 160, objectFit: "cover" }}
              />
              <ProductDiscountBadge product={product} />
              <div style={{ padding: 16 }}>
                {(() => {
                  const status = getProductApprovalStatus(product);
                  return (
                    <span
                      className={`dashboard-badge dashboard-badge--${getProductApprovalBadgeTone(status)}`}
                      style={{ marginBottom: 8, display: "inline-block" }}
                    >
                      {getProductApprovalLabel(status)}
                    </span>
                  );
                })()}
                {product.approvalStatus === "rejected" && product.rejectionReason && (
                  <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--dash-danger)" }}>
                    {product.rejectionReason}
                  </p>
                )}
                {product.approvalStatus === "pending" && (
                  <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--dash-muted)" }}>
                    Waiting for admin approval before it appears on the marketplace.
                  </p>
                )}
                {isLowStock(product) && (
                  <span className="dashboard-badge dashboard-badge--warning" style={{ marginBottom: 8 }}>
                    Low stock: {product.quantity}
                  </span>
                )}
                <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>{product.name}</h3>
                <p style={{ margin: "0 0 4px", color: "var(--dash-muted)", fontSize: 13 }}>{product.category}</p>
                <ProductPriceDisplay product={product} size="card" />
                <div style={{ marginTop: 12 }}>
                <Link to={`/merchant/products/${product.slug || product._id}`} className="dashboard-btn dashboard-btn--secondary" style={{ width: "100%" }}>
                  View & edit
                </Link>
              </div>
              </div>
            </div>
          );
          const key = product._id;
          if (filteredProducts.length === index + 1) {
            return <div key={key} ref={lastRef}>{card}</div>;
          }
          return <div key={key}>{card}</div>;
        })}
      </div>
      {isFetchingNextPage && <Spinner />}
    </>
  );
};

export default MerchantProductsPage;
